import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PROVIDERS = ["openai", "anthropic", "gemini"] as const;

interface AiSettings {
  enabled: boolean;
  provider: string;
  model: string;
  temperature: number;
  contextMessageLimit: number;
  systemPrompt: string;
  entityPermissions?: Record<string, Record<string, boolean>>;
  purposeModels?: Record<string, unknown>;
}

interface LlmKey {
  provider: string;
  keyHint: string | null;
  updatedAt: string;
}

/**
 * Per-instance AI model settings + LLM API keys. Rendered as a block on the
 * instance detail page; platform-staff-only (behind the admin routes). One
 * shared component reused for every instance — the single place to update the
 * UX for all instances.
 */
export function InstanceAiSettingsSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const qc = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["instance", name, "ai-settings"],
    queryFn: () =>
      api.get<{ settings: AiSettings }>(
        `/api/admin/instances/${name}/ai-settings`
      ),
  });

  const [form, setForm] = React.useState<AiSettings | null>(null);
  React.useEffect(() => {
    if (settingsQuery.data?.settings) setForm(settingsQuery.data.settings);
  }, [settingsQuery.data]);

  const saveSettings = useMutation({
    mutationFn: (payload: AiSettings) =>
      api.put(`/api/admin/instances/${name}/ai-settings`, payload),
    onSuccess: () => {
      toast.success(t("aiSettings.saved"));
      qc.invalidateQueries({ queryKey: ["instance", name, "ai-settings"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("aiSettings.saveFailed")),
  });

  const set = <K extends keyof AiSettings>(key: K, value: AiSettings[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("aiSettings.title")}</CardTitle>
          <CardDescription>
            {t("aiSettings.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsQuery.isLoading || !form ? (
            <p className="text-sm text-muted-foreground">{t("aiSettings.loading")}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="ai-enabled">{t("aiSettings.enabled")}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("aiSettings.enabledHint")}
                  </p>
                </div>
                <Switch
                  id="ai-enabled"
                  checked={form.enabled}
                  onCheckedChange={(c) => set("enabled", c)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ai-provider">{t("aiSettings.provider")}</Label>
                  <Select
                    value={form.provider}
                    onValueChange={(v) => set("provider", v)}
                  >
                    <SelectTrigger id="ai-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-model">{t("aiSettings.model")}</Label>
                  <Input
                    id="ai-model"
                    value={form.model}
                    onChange={(e) => set("model", e.target.value)}
                    placeholder="gpt-4o-mini"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-temp">{t("aiSettings.temperature")}</Label>
                  <Input
                    id="ai-temp"
                    type="number"
                    min={0}
                    max={2}
                    step={0.05}
                    value={form.temperature}
                    onChange={(e) =>
                      set("temperature", Number(e.target.value))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ai-ctx">{t("aiSettings.contextLimit")}</Label>
                  <Input
                    id="ai-ctx"
                    type="number"
                    min={2}
                    max={200}
                    value={form.contextMessageLimit}
                    onChange={(e) =>
                      set("contextMessageLimit", Number(e.target.value))
                    }
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ai-prompt">{t("aiSettings.systemPrompt")}</Label>
                <Textarea
                  id="ai-prompt"
                  value={form.systemPrompt}
                  onChange={(e) => set("systemPrompt", e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => form && saveSettings.mutate(form)}
                  disabled={saveSettings.isPending}
                >
                  {t("aiSettings.save")}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <LlmKeysCard name={name} />
    </div>
  );
}

/** Per-provider LLM API keys — set / replace / remove (never shows plaintext). */
function LlmKeysCard({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const qc = useQueryClient();
  const keysQuery = useQuery({
    queryKey: ["instance", name, "llm-keys"],
    queryFn: () =>
      api.get<{ keys: LlmKey[]; providers: string[] }>(
        `/api/admin/instances/${name}/llm-keys`
      ),
  });
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["instance", name, "llm-keys"] });

  const setKey = useMutation({
    mutationFn: ({ provider, key }: { provider: string; key: string }) =>
      api.put(`/api/admin/instances/${name}/llm-keys/${provider}`, { key }),
    onSuccess: (_d, v) => {
      toast.success(t("llmKeys.saved", { provider: v.provider }));
      setDrafts((d) => ({ ...d, [v.provider]: "" }));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("llmKeys.saveFailed")),
  });

  const removeKey = useMutation({
    mutationFn: (provider: string) =>
      api.del(`/api/admin/instances/${name}/llm-keys/${provider}`),
    onSuccess: () => {
      toast.success(t("llmKeys.removed"));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("llmKeys.removeFailed")),
  });

  const providers = keysQuery.data?.providers ?? PROVIDERS;
  const byProvider = new Map(
    (keysQuery.data?.keys ?? []).map((k) => [k.provider, k])
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("llmKeys.title")}</CardTitle>
        <CardDescription>
          {t("llmKeys.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {keysQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">{t("llmKeys.loading")}</p>
        ) : (
          providers.map((provider) => {
            const existing = byProvider.get(provider);
            return (
              <div key={provider} className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor={`key-${provider}`}>{provider}</Label>
                  {existing ? (
                    <span className="text-xs text-muted-foreground">
                      {t("llmKeys.set", { hint: existing.keyHint })}
                    </span>
                  ) : (
                    <span className="text-xs text-destructive">{t("llmKeys.notSet")}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    id={`key-${provider}`}
                    type="password"
                    autoComplete="off"
                    value={drafts[provider] ?? ""}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [provider]: e.target.value }))
                    }
                    placeholder={existing ? t("llmKeys.replacePlaceholder") : t("llmKeys.enterPlaceholder")}
                  />
                  <Button
                    onClick={() =>
                      setKey.mutate({ provider, key: drafts[provider] ?? "" })
                    }
                    disabled={
                      setKey.isPending || (drafts[provider] ?? "").length < 8
                    }
                  >
                    {t("llmKeys.save")}
                  </Button>
                  {existing && (
                    <Button
                      variant="outline"
                      onClick={() => removeKey.mutate(provider)}
                      disabled={removeKey.isPending}
                    >
                      {t("llmKeys.remove")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
